import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { ArrowLeft, Award, Baby, BarChart3, BookOpen, Brain, Briefcase, Calendar, CheckCircle, ClipboardList, Clock, Eye, FileText, GraduationCap, Home, Mail, MapPin, Phone, Play, Shield, Star, TrendingUp, User, UserCheck, Users } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalObserverDetail = () => {
  const navigate = useNavigate();
  const { observerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [observer, setObserver] = useState(null);
  const [stats, setStats] = useState(null);
  const [children, setChildren] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [observerId, navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      
      const [observerRes, statsRes, childrenRes, sessionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/observer/${observerId}`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/observer/${observerId}/stats`, { params: { token } }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/principal/observer/${observerId}/children`, { params: { token } }).catch(() => ({ data: { children: [] } })),
        axios.get(`${BACKEND_URL}/api/principal/observer/${observerId}/sessions`, { params: { token, limit: 10 } }).catch(() => ({ data: { sessions: [] } }))
      ]);
      
      setObserver(observerRes.data);
      setStats(statsRes.data);
      setChildren(childrenRes.data.children || []);
      setRecentSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error('Error loading observer data:', error);
      alert('Failed to load observer details');
      navigate('/principal/observers');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  if (!observer) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 text-center">
          <p className="text-slate-500">Observer not found</p>
          <Button onClick={() => navigate('/principal/observers')} className="mt-4">
            Back to Observers
          </Button>
        </div>
      </div>
    );
  }

  const avgScore = stats?.average_score || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="observer-detail-page">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          {/* Header Card */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center text-4xl font-bold">
                  {observer.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold">{observer.name}</h1>
                  <div className="flex items-center gap-4 mt-2 text-emerald-100">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {observer.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {observer.phone || 'N/A'}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <span className={`px-3 py-1 rounded-full text-sm ${observer.status === 'approved' ? 'bg-green-400/30' : 'bg-yellow-400/30'}`}>
                      {observer.status || 'Active'}
                    </span>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                      {children.length} Children Assigned
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold ${getScoreColor(avgScore)}`}>
                    {avgScore.toFixed ? avgScore.toFixed(1) : avgScore}
                  </div>
                  <p className="text-sm mt-1 text-emerald-100">Avg Score</p>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Personal Details */}
            <div className="space-y-6">
              {/* Contact Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-sanjaya-navy" />
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{observer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{observer.phone || 'Not provided'}</p>
                    </div>
                  </div>
                  {observer.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Address</p>
                        <p className="font-medium text-sm">{observer.address}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Qualifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-purple-600" />
                    Qualifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {observer.qualification && (
                    <div>
                      <p className="text-sm text-slate-500">Education</p>
                      <p className="font-medium">{observer.qualification}</p>
                    </div>
                  )}
                  {observer.current_job && (
                    <div>
                      <p className="text-sm text-slate-500">Current Role</p>
                      <p className="font-medium">{observer.current_job}</p>
                    </div>
                  )}
                  {observer.experience && (
                    <div>
                      <p className="text-sm text-slate-500">Experience</p>
                      <p className="font-medium text-sm">{observer.experience}</p>
                    </div>
                  )}
                  {observer.interests && (
                    <div>
                      <p className="text-sm text-slate-500">Interests</p>
                      <p className="font-medium text-sm">{observer.interests}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Availability */}
              {observer.availability && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-600" />
                      Availability
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {observer.availability.timings && (
                      <div>
                        <p className="text-sm text-slate-500">Timings</p>
                        <p className="font-medium">{observer.availability.timings}</p>
                      </div>
                    )}
                    {observer.availability.days && (
                      <div>
                        <p className="text-sm text-slate-500">Days</p>
                        <p className="font-medium">{observer.availability.days}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Middle Column - Performance Stats */}
            <div className="space-y-6">
              {/* Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-3xl font-bold text-sanjaya-navy">{stats?.total_sessions || 0}</p>
                      <p className="text-sm text-slate-600">Total Sessions</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{children.length}</p>
                      <p className="text-sm text-slate-600">Children</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-3xl font-bold text-purple-600">{stats?.total_audits_conducted || 0}</p>
                      <p className="text-sm text-slate-600">Audits Done</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-3xl font-bold text-amber-600">{stats?.approval_rate || 95}%</p>
                      <p className="text-sm text-slate-600">Approval Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quality Breakdown */}
              {stats?.scores_breakdown && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-600" />
                      Session Quality
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          Excellent
                        </span>
                        <span className="font-bold text-green-600">{stats.scores_breakdown.excellent || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <span className="flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-600" />
                          Good
                        </span>
                        <span className="font-bold text-yellow-600">{stats.scores_breakdown.good || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <span className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-red-600" />
                          Needs Improvement
                        </span>
                        <span className="font-bold text-red-600">{stats.scores_breakdown.needs_improvement || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Certifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-600" />
                    Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(stats?.total_sessions || 0) >= 100 && (
                      <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-purple-900">Master Observer</p>
                          <p className="text-xs text-purple-600">100+ sessions completed</p>
                        </div>
                      </div>
                    )}
                    {(stats?.total_sessions || 0) >= 50 && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                          <Star className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-900">Gold Certified</p>
                          <p className="text-xs text-amber-600">50+ sessions completed</p>
                        </div>
                      </div>
                    )}
                    {(stats?.total_sessions || 0) >= 25 && (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">Silver Certified</p>
                          <p className="text-xs text-slate-600">25+ sessions completed</p>
                        </div>
                      </div>
                    )}
                    {(stats?.total_sessions || 0) < 25 && (
                      <div className="text-center py-4 text-slate-500">
                        <p className="text-sm">No certifications yet</p>
                        <p className="text-xs mt-1">Complete 25+ sessions to earn first certification</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Assigned Children & Sessions */}
            <div className="space-y-6">
              {/* Assigned Children */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Baby className="w-5 h-5 text-sanjaya-navy" />
                    Assigned Children ({children.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {children.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No children assigned</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {children.map((child, idx) => (
                        <div 
                          key={child.id || idx}
                          className="p-3 bg-slate-50 rounded-lg border hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/principal/child/${child.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Baby className="w-5 h-5 text-sanjaya-navy" />
                              </div>
                              <div>
                                <p className="font-medium">{child.name}</p>
                                <p className="text-xs text-slate-500">Age: {child.age} • {child.class || 'N/A'}</p>
                              </div>
                            </div>
                            <Eye className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Sessions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-rose-600" />
                    Recent Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentSessions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No sessions recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {recentSessions.map((session, idx) => (
                        <div 
                          key={session.id || idx}
                          className="p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 cursor-pointer"
                          onClick={() => navigate(`/principal/recordings?session=${session.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{session.child_name}</p>
                              <p className="text-xs text-slate-500">{session.date || session.session_date}</p>
                            </div>
                            <Play className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrincipalObserverDetail;
